import { StyleSheet, View } from 'react-native';
import type { StyleProp, TextStyle, ViewStyle } from 'react-native';

import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/iconNames';
import { useTheme } from '../theme/ThemeProvider';
import type { ThemeTokens } from '../theme/tokens';
import { Text } from './Text';

export type BadgeTone = 'neutral' | 'primary' | 'info' | 'success' | 'warning' | 'danger';

export type BadgeVariant = 'solid' | 'subtle';

export type BadgeSize = 'sm' | 'md';

export type BadgeProps = {
  label: string;
  tone?: BadgeTone;
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: IconName;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

type BadgePalette = { solid: string; onSolid: string; subtle: string; onSubtle: string };

function palette(theme: ThemeTokens, tone: BadgeTone): BadgePalette {
  const colors = theme.colors;
  switch (tone) {
    case 'primary':
      return {
        solid: colors.primary,
        onSolid: colors.onPrimary,
        subtle: colors.surfaceAlt,
        onSubtle: colors.primary,
      };
    case 'info':
      return {
        solid: colors.info,
        onSolid: colors.onInfo,
        subtle: colors.infoSubtle,
        onSubtle: colors.onInfoSubtle,
      };
    case 'success':
      return {
        solid: colors.success,
        onSolid: colors.onSuccess,
        subtle: colors.successSubtle,
        onSubtle: colors.onSuccessSubtle,
      };
    case 'warning':
      return {
        solid: colors.warning,
        onSolid: colors.onWarning,
        subtle: colors.warningSubtle,
        onSubtle: colors.onWarningSubtle,
      };
    case 'danger':
      return {
        solid: colors.danger,
        onSolid: colors.onDanger,
        subtle: colors.dangerSubtle,
        onSubtle: colors.onDangerSubtle,
      };
    case 'neutral':
    default:
      return {
        solid: colors.dark,
        onSolid: colors.textInverse,
        subtle: colors.surfaceAlt,
        onSubtle: colors.text,
      };
  }
}

export function Badge({
  label,
  tone = 'neutral',
  variant = 'subtle',
  size = 'md',
  icon,
  style,
  testID,
}: BadgeProps) {
  const theme = useTheme();
  const colors = palette(theme, tone);
  const foreground = variant === 'solid' ? colors.onSolid : colors.onSubtle;

  const containerStyle: ViewStyle = {
    backgroundColor: variant === 'solid' ? colors.solid : colors.subtle,
    borderRadius: theme.radius.pill,
    paddingVertical: size === 'sm' ? theme.spacing(0.5) : theme.spacing(1),
    paddingHorizontal: size === 'sm' ? theme.spacing(1.5) : theme.spacing(2),
    gap: theme.spacing(1),
  };

  const labelStyle: TextStyle = {
    fontSize: size === 'sm' ? theme.fontSize.xs : theme.fontSize.sm,
    color: foreground,
  };

  return (
    <View style={[styles.badge, containerStyle, style]} testID={testID}>
      {icon !== undefined ? (
        <Icon name={icon} size={size === 'sm' ? 'xs' : 'sm'} color={foreground} />
      ) : null}
      <Text weight="semibold" style={labelStyle} numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  badge: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start' },
});
